import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef, useState } from "react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { Modal } from "./Modal";
import { Terminal } from "lucide-react";
import { useIsMobile } from "./ui/use-mobile";

const experiences = [
  {
    title: "PayPal",
    company: "Software Engineer",
    year: "PRESENT",
    location: "San Jose, CA",
    period: "2025—Present",
    image: "/paypal.png",
    description:
      "Building the AI Software Factory at PayPal to automate the SDLC",
    responsibilities: [
      "Engineering an AI-SDLC system that autonomously takes Jira tickets to tested and reviewed Pull Requests, cutting LLM token spend by upwards of 23% across PayPal.",
      "Designing a multi-harness execution architecture on Google Kubernetes Environment with dynamic harness selection and on-demand execution, centralizing orchestration of 3+ harnesses across PayPal.",
      "Developing an agentic platform that decomposes product features and timelines into granular epics and stories aligned with leadership requirements through spec-driven workflows, accelerating the first half of the PDLC by 30%+ company-wide.",
      "Architected platform infrastructure from a single GKE cluster into isolated development, staging, and production environments utilizing Terraform and Atlantis, creating standards for regular rollouts and enabling faster parallel development."
    ],
    technologies: [
      "Next.js",
      "TypeScript",
      "Claude Agent SDK",
      "LiteLLM",
      "AWS",
      "GCP",
      "GKE",
      "Kubernetes",
      "Terraform",
      "Atlantis",
      "OpenTelemetry",
      "Datadog"
    ],
  },
  {
    title: "Denari AI",
    company: "Software Engineer Apprentice",
    year: "2025",
    location: "San Jose, CA",
    period: "2025-2025",
    image: "/denari.png",
    description:
      "Engineered a RAG pipeline to accurately answer CPA and tax-research related questions.",
    responsibilities: [
      "Crafted a Retrieval Augmented Generation (RAG) model by utilizing Python with OpenAI LLMs to generate appropriate responses to questions.",
      "Leveraged TimescaleDB and PostgreSQL with extended vector support to store and retrieve vector embeddings, allowing synthesis from 50,000+ Tax documents.",
      "Fine-tuned the overall pipeline by optimizing Unstructured-IO hyperparameters and TimescaleDB configurations to achieve optimal performance throughout the pipeline, generating each accurate response in under 1 second.",
      "Enabled accounting firms with tax research that is around 20% more accurate than GPT and passing any full-length CPA exam."
    ],
    technologies: [
      "Python",
      "PostgreSQL",
      "TimescaleDB",
      "Docker",
      "AWS S3",
      "OpenAI LLMs",
      "Unstructured-IO",
      "Jira",
      "Git"
    ],
  },
  {
    title: "Kohl's",
    company: "Software Engineer Intern",
    year: "2024",
    location: "San Ramon, CA",
    period: "2024—2024",
    image:
      "/kohls.png",
    description:
      "Designed and Developed a full-stack feature for Kohls.com.",
    responsibilities: [
      "Developed a streamlined pricing feature end-to-end for Kohl’s search & browse experience, driving $20M+ in revenue.",
      "Engineered the frontend with JavaScript by working in a Test-Driven Development Environment using Cypress in a Node.js architecture to deliver the pricing feature, boosting sales by 15% and delivering product 3 weeks earlier.",
      "Designed and implemented a dedicated Spring Boot microservice for this feature, replacing legacy microservice and achieving sub-100ms response times.",
    ],
    technologies: [
      "JavaScript",
      "Node.JS",
      "Java",
      "Cypress",
      "Spring Boot",
      "Jira",
      "Git"
    ],
  },
  {
    title: "Honeywell",
    company: "Software Engineer Intern",
    year: "2023",
    location: "Pittsburgh, PA",
    period: "2023—2023",
    image:
      "/honeywell.png",
    description:
      "Created a responsive prototype to serve as the in-house depalletizing assistance software solution for 20+ customer sites.",
    responsibilities: [
      "Designed and developed frontend using Azure Power Apps and TypeScript",
      "Generated vital data pertaining to system performance by utilizing PLC, C++, and Docker, improving the quality of KPI files to exceed customer satisfaction standards by +10%.",
      "Conducted regression testing to resolve 50+ crucial tests pertaining to motion planning by using Jira XRay and utilizing PLC with C++ codebase, optimizing overall functionality of the robotics software"
    ],
    technologies: ["TypeScript", "Azure", "Docker", "C++", "Jira"],
  },
];

export function ExperiencesSection() {
  const [selectedExperience, setSelectedExperience] = useState<
    (typeof experiences)[0] | null
  >(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: wrapperRef,
    offset: ["start start", "end start"],
  });
  const prefersReduced = useReducedMotion();
  const isMobile = useIsMobile();
  const factor = prefersReduced || isMobile ? 0.5 : 1;

  const scale = useTransform(scrollYProgress, [0, 1], [1, 1 - 0.1 * factor]);
  const opacity = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [1, 0.5, 0],
  );
  // Refined depth-based parallax for background elements (no rotation)
  const bgLeftX = useTransform(
    scrollYProgress,
    [0, 1],
    [0, -120 * factor],
  );
  const bgRightX = useTransform(
    scrollYProgress,
    [0, 1],
    [0, 120 * factor],
  );
  const bgLeftScale = useTransform(
    scrollYProgress,
    [0, 1],
    [1, 1.15],
  );
  const bgRightScale = useTransform(
    scrollYProgress,
    [0, 1],
    [1, 1.12],
  );

  // Subtle additional depth layers
  const bgCenterX = useTransform(scrollYProgress, [0, 1], [0, 80 * factor]);
  const bgCenterY = useTransform(scrollYProgress, [0, 1], [0, -100 * factor]);
  const bgCenterScale = useTransform(scrollYProgress, [0, 1], [0.9, 1.1]);

  // Title parallax
  const titleY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, -120 * factor],
  );

  return (
    <>
      <div ref={wrapperRef} className="relative">
        <section
          className="min-h-screen sticky top-0 py-16 sm:py-24 md:py-32 lg:py-40 px-4 sm:px-6 md:px-8 lg:px-12 bg-[#0a0a0a] text-[#eceff4] overflow-hidden industrial-grid"
          style={{ zIndex: 2 }}
        >
          <div className="scanline" />

          <motion.div
            style={{ scale, opacity }}
            className="max-w-7xl mx-auto relative z-10"
          >
            {/* Section label */}
            <div className="absolute -top-12 sm:-top-16 md:-top-20 left-0 flex items-center gap-4">
              <Terminal className="w-4 h-4 text-[#d8dee9]/20" />
              <span className="mono text-xs tracking-[0.4em] text-[#d8dee9]/20">
                SECTION_01
              </span>
            </div>

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{
                duration: 1,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              viewport={{ once: true, margin: "-100px" }}
              className="mb-16 sm:mb-20 md:mb-24 lg:mb-32"
              style={{ y: titleY }}
            >
              <h2
                className="text-[4rem] sm:text-[5rem] md:text-[6rem] lg:text-[8rem] xl:text-[12rem] leading-[0.85] grotesk tracking-tighter"
                style={{
                  fontFamily: "var(--font-grotesk)",
                  fontWeight: 700,
                }}
              >
                Work
              </h2>
              <div className="flex items-center gap-4 mt-4">
                <div className="w-24 h-px bg-[#eceff4]" />
                <span className="mono text-xs text-[#d8dee9]/40">
                  PROFESSIONAL HISTORY
                </span>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {experiences.map((experience, index) => (
                <ExperienceCard
                  key={index}
                  experience={experience}
                  index={index}
                  onClick={() =>
                    setSelectedExperience(experience)
                  }
                />
              ))}
            </div>
          </motion.div>

          {/* Refined parallax background elements with subtle depth */}
          <motion.div
            style={{
              x: bgLeftX,
              y: useTransform(
                scrollYProgress,
                [0, 1],
                [200 * factor, -500 * factor],
              ),
              scale: bgLeftScale,
              opacity: useTransform(
                scrollYProgress,
                [0, 1],
                [0.15, 0],
              ),
            }}
            className="absolute top-0 left-0 w-[900px] h-[900px] border-2 border-[#d8dee9]/10 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0"
          />
          <motion.div
            style={{
              x: bgRightX,
              y: useTransform(
                scrollYProgress,
                [0, 1],
                [-200 * factor, 500 * factor],
              ),
              scale: bgRightScale,
              opacity: useTransform(
                scrollYProgress,
                [0, 1],
                [0.15, 0],
              ),
            }}
            className="absolute bottom-0 right-0 w-[700px] h-[700px] border-2 border-[#d8dee9]/10 translate-x-1/2 translate-y-1/2 pointer-events-none z-0"
          />

          {/* Subtle center depth layer */}
          <motion.div
            style={{
              x: bgCenterX,
              y: bgCenterY,
              scale: bgCenterScale,
              opacity: useTransform(scrollYProgress, [0, 1], [0.1, 0]),
            }}
            className="absolute top-1/2 left-1/2 w-[600px] h-[600px] border border-[#d8dee9]/5 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0"
          />

          {/* Refined Cross pattern */}
          <motion.div
            style={{
              y: useTransform(
                scrollYProgress,
                [0, 1],
                [0, 200 * factor],
              ),
              opacity: useTransform(scrollYProgress, [0, 1], [0.05, 0]),
            }}
            className="absolute top-1/2 left-1/2 w-screen h-px bg-[#eceff4]/5 pointer-events-none z-0"
          />
          <motion.div
            style={{
              x: useTransform(
                scrollYProgress,
                [0, 1],
                [0, -200 * factor],
              ),
              opacity: useTransform(scrollYProgress, [0, 1], [0.05, 0]),
            }}
            className="absolute top-1/2 left-1/2 w-px h-screen bg-[#eceff4]/5 pointer-events-none z-0"
          />
        </section>
      </div>

      {/* Modal */}
      <Modal
        isOpen={!!selectedExperience}
        onClose={() => setSelectedExperience(null)}
      >
        {selectedExperience && (
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="flex items-start gap-4 pb-4 border-b border-[#d8dee9]/10"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.35, duration: 0.3 }}
                className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 overflow-hidden bg-neutral-900 border border-[#d8dee9]/10"
              >
                <ImageWithFallback
                  src={selectedExperience.image}
                  alt={selectedExperience.title}
                  className="w-full h-full object-contain"
                />
              </motion.div>
              <div className="flex-1 min-w-0">
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                  className="mono text-[10px] tracking-[0.2em] uppercase text-[#d8dee9]/40 mb-1"
                >
                  {selectedExperience.period}
                </motion.div>
                <motion.h2
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.45, duration: 0.3 }}
                  className="text-xl sm:text-2xl lg:text-3xl grotesk tracking-tight font-bold text-[#eceff4] mb-2"
                  style={{ fontFamily: "var(--font-grotesk)" }}
                >
                  {selectedExperience.company}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                  className="text-sm text-neutral-400 leading-snug"
                >
                  {selectedExperience.description}
                </motion.p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.4 }}
            >
              <h4 className="mono text-[10px] tracking-[0.2em] mb-3 text-[#d8dee9]/40">
                Key Responsibilities
              </h4>
              <ul className="space-y-2.5">
                {selectedExperience.responsibilities.map(
                  (responsibility, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 0.6 + index * 0.05,
                        duration: 0.3,
                      }}
                      className="text-sm text-neutral-400 font-light flex items-start gap-2.5"
                    >
                      <span className="text-[#d8dee9]/20 mt-0.5 mono text-[10px] flex-shrink-0">
                        [{String(index + 1).padStart(2, "0")}]
                      </span>
                      <span className="leading-relaxed">{responsibility}</span>
                    </motion.li>
                  ),
                )}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.4 }}
            >
              <h4 className="mono text-[10px] tracking-[0.2em] mb-3 text-[#d8dee9]/40">
                Tech Stack
              </h4>
              <div className="flex flex-wrap gap-2">
                {selectedExperience.technologies.map(
                  (tech, index) => (
                    <motion.span
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        delay: 0.75 + index * 0.03,
                        duration: 0.2,
                        type: "spring",
                        stiffness: 200,
                      }}
                      whileHover={{ scale: 1.05 }}
                      className="px-2.5 py-1 border border-[#d8dee9]/20 mono text-[10px] text-neutral-300 hover:bg-[#eceff4]/5 transition-colors cursor-default"
                    >
                      {tech}
                    </motion.span>
                  ),
                )}
              </div>
            </motion.div>
          </div>
        )}
      </Modal>
    </>
  );
}

function ExperienceCard({
  experience,
  index,
  onClick,
}: {
  experience: (typeof experiences)[0];
  index: number;
  onClick: () => void;
}) {
  const cardRef = useRef<HTMLButtonElement>(null);
  const prefersReduced = useReducedMotion();
  const isMobile = useIsMobile();
  const factor = prefersReduced || isMobile ? 0.5 : 1;
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });

  // Refined depth-based parallax for experience cards
  const depthMultiplier = index % 3 === 0 ? 0.9 : index % 3 === 1 ? 1.0 : 1.1;
  const y = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [80 * factor * depthMultiplier, 0, -80 * factor * depthMultiplier],
  );
  const x = useTransform(
    scrollYProgress,
    [0, 1],
    [(index % 2 === 0 ? -1 : 1) * 20 * factor, (index % 2 === 0 ? 1 : -1) * 20 * factor],
  );
  const cardScale = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [0.97 + (index * 0.005), 1, 0.98 - (index * 0.003)],
  );
  const imageY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, -30 * factor * depthMultiplier],
  );
  const imageScale = useTransform(
    scrollYProgress,
    [0, 1],
    [1, 1.05 * depthMultiplier],
  );

  return (
    <motion.button
      ref={cardRef}
      onClick={onClick}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.8,
        delay: index * 0.1,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      viewport={{ once: true, margin: "-50px" }}
      className="group text-left cursor-pointer relative"
    >
      <motion.div style={{ y, x, scale: cardScale }}>
        {/* Corner markers */}
        <div className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-[#d8dee9]/20 group-hover:border-[#eceff4] transition-colors z-10" />
        <div className="absolute -top-2 -right-2 w-4 h-4 border-t-2 border-r-2 border-[#d8dee9]/20 group-hover:border-[#eceff4] transition-colors z-10" />
        <div className="absolute -bottom-2 -left-2 w-4 h-4 border-b-2 border-l-2 border-[#d8dee9]/20 group-hover:border-[#eceff4] transition-colors z-10" />
        <div className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 border-[#d8dee9]/20 group-hover:border-[#eceff4] transition-colors z-10" />

        <div className="relative aspect-[4/3] overflow-hidden bg-black mb-6 border-2 border-[#d8dee9]/10 group-hover:border-[#e5e9f0]/30 transition-colors">
          <motion.div
            style={{ y: imageY, scale: imageScale }}
            className="w-full h-[110%]"
          >
            <ImageWithFallback
              src={experience.image}
              alt={experience.title}
              className="w-full h-full object-contain transition-all duration-700 filter grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100"
            />
          </motion.div>

          {/* Overlay grid */}
          <div className="absolute inset-0 industrial-grid opacity-30" />

          {/* Label overlay */}
          <div className="absolute bottom-4 left-4 mono text-xs text-[#eceff4]/60 bg-black/50 px-2 py-1">
            EXP_{String(index + 1).padStart(2, "0")}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="mono text-xs tracking-[0.2em] uppercase text-[#d8dee9]/40">
              {experience.company}
            </p>
            <span className="mono text-xs text-[#eceff4]/30">
              {experience.year}
            </span>
          </div>
          <h3 className="text-xl lg:text-2xl grotesk tracking-tight group-hover:text-[#eceff4]/60 transition-colors">
            {experience.title}
          </h3>
          <div className="w-12 h-px bg-[#eceff4]/20 group-hover:w-24 group-hover:bg-[#eceff4] transition-all duration-500" />
        </div>
      </motion.div>
    </motion.button>
  );
}